// src/components/tuition/DirectClassroomJoin.jsx
import React, { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import {
  FaCheckCircle,
  FaClock,
  FaCopy,
  FaDoorOpen,
  FaMicrophone,
  FaMicrophoneSlash,
  FaPhoneSlash,
  FaSignOutAlt,
  FaUser,
  FaUsers,
  FaVideo,
  FaVideoSlash,
  FaExternalLinkAlt,
  FaComments,
  FaPaperPlane,
  FaExclamationTriangle,
  FaTimesCircle,
  FaUserClock,
  FaArrowLeft,
  FaShieldAlt,
  FaInfoCircle,
} from "react-icons/fa";
import { toast } from "react-toastify";
import { useAppTheme } from "../../context/AppThemeContext";
import api from "../../api/httpClient";
import {
  cancelClassroomAdmission,
  getRoomSettings,
  joinAdmittedGuest,
  requestClassroomAdmission,
  subscribeToAdmissionStatus,
  validateMeetingCode,
} from "../../services/classroomWaitingRoomService";
import HostWaitingRoomControls from "./HostWaitingRoomControls";
import ClassroomManagementStudio from "./ClassroomManagementStudio";
import LiveKitClassroom from "../classroom/LiveKitClassroom";
import ClassroomDeviceSetup from "../classroom/ClassroomDeviceSetup";
import {
  normalizeRole,
  ROLE_ADMIN,
  ROLE_PRINCIPAL,
  ROLE_SCHOOL,
  ROLE_STAFF,
  ROLE_TEACHER,
} from "../../utils/authRole";

const ClassNXBrandSignature = ({ compact = false }) => (
  <div
    className={`inline-flex items-center border border-sky-200/80 bg-white/90 shadow-lg shadow-sky-950/5 backdrop-blur-xl dark:border-sky-500/20 dark:bg-slate-900/90 ${
      compact ? "gap-2 rounded-full px-2.5 py-1" : "gap-3 rounded-2xl px-4 py-3"
    }`}
    aria-label="Powered by ClassNX"
  >
    <span className={`relative shrink-0 ${compact ? "h-6 w-6" : "h-9 w-9"}`}>
      <span className="absolute inset-0 rounded-xl bg-sky-500/25 blur-md" />
      <img
        src={`${process.env.PUBLIC_URL || ""}/logo.png`}
        alt=""
        className="relative h-full w-full rounded-xl object-cover ring-1 ring-white/80 dark:ring-slate-700"
      />
    </span>
    <span className="text-left leading-none">
      <span className={`block font-bold uppercase text-slate-400 ${compact ? "text-[7px] tracking-[0.18em]" : "text-[8px] tracking-[0.22em]"}`}>
        Powered by
      </span>
      <span className={`mt-1 block font-black tracking-tight text-slate-900 dark:text-white ${compact ? "text-xs" : "text-base"}`}>
        Class<span className="text-sky-600 dark:text-sky-400">NX</span>
      </span>
    </span>
    {!compact && (
      <span className="ml-1 border-l border-slate-200 pl-3 text-left dark:border-slate-700">
        <span className="flex items-center gap-1 text-[10px] font-bold text-emerald-600 dark:text-emerald-400">
          <FaShieldAlt /> Secure classroom
        </span>
        <span className="mt-0.5 block text-[9px] text-slate-400">Live learning infrastructure</span>
      </span>
    )}
  </div>
);

export default function DirectClassroomJoin() {
  const { isDarkMode } = useAppTheme();
  const { roomCode } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const scheduledClass = location.state?.scheduledClass || null;
  const scheduledStudent = location.state?.student || null;
  const isScheduledEntry = Boolean(roomCode);
  // Enter-room actions from a tuition owner/teacher portal explicitly request
  // the authenticated host path. This flag grants nothing by itself: the
  // backend still verifies tenant and assigned-teacher ownership at /start.
  const isRequestedHostEntry = new URLSearchParams(location.search).get("asHost") === "1";

  // Step: "entry" | "waiting-room" | "in-class" | "denied" | "left"
  const [step, setStep] = useState("entry");

  // Entry Form State
  const [meetingCode, setMeetingCode] = useState(() => (roomCode ? String(roomCode).trim().toUpperCase() : ""));
  const [studentName, setStudentName] = useState(() => {
    // A logged-in owner/teacher must see their authenticated display name.
    // The saved direct-meeting name is only a guest convenience and can be
    // stale from a previous public join.
    return scheduledStudent?.name || localStorage.getItem("currentUserName") || localStorage.getItem("directMeeting:studentName") || "";
  });
  const [studentRollOrPhone, setStudentRollOrPhone] = useState(() => scheduledStudent?.rollNo || scheduledStudent?.mobile || "");
  const [micActive, setMicActive] = useState(true);
  const [camActive, setCamActive] = useState(true);
  const [previewStream, setPreviewStream] = useState(null);
  const previewVideoRef = useRef(null);
  const joiningRoomRef = useRef(false);

  // LiveKit Media Credentials & Waiting Room State
  const [guestToken, setGuestToken] = useState("");
  const [waitingRequestId, setWaitingRequestId] = useState(null);
  const [waitingSeconds, setWaitingSeconds] = useState(0);
  const [denialReason, setDenialReason] = useState("");
  const [admissionApproved, setAdmissionApproved] = useState(false);
  const [joinError, setJoinError] = useState("");
  const [joinRetryNonce, setJoinRetryNonce] = useState(0);

  // In-Classroom State
  const [sessionStartTime, setSessionStartTime] = useState(null);
  const [durationSeconds, setDurationSeconds] = useState(0);
  const [chatMessages, setChatMessages] = useState([
    { sender: "System", text: "Welcome to ClassNX Live Classroom. Please maintain discipline.", time: "Just now" },
  ]);
  const [newChatText, setNewChatText] = useState("");
  const [showChat, setShowChat] = useState(false);
  const [showParticipants, setShowParticipants] = useState(false);
  const [liveParticipants, setLiveParticipants] = useState([]);
  const classroomDataSenderRef = useRef(null);

  // Completed Attendance Record
  const [completedRecord, setCompletedRecord] = useState(null);

  // The application persists the authenticated role under `user`. `userRole`
  // is legacy-only, so read both and normalize plain strings or JSON objects.
  const storedUserRole = (() => {
    const raw = localStorage.getItem("user") || localStorage.getItem("userRole") || "";
    try {
      const parsed = JSON.parse(raw);
      return parsed?.role || parsed?.userRole || raw;
    } catch {
      return raw;
    }
  })();
  const currentUserRole = normalizeRole(storedUserRole);
  const isTeacherUser = [ROLE_ADMIN, ROLE_SCHOOL, ROLE_PRINCIPAL, ROLE_TEACHER, ROLE_STAFF].includes(currentUserRole);
  const isHostEntry = isRequestedHostEntry || isTeacherUser;

  // Find matching class and validate code in real-time
  const [codeValidation, setCodeValidation] = useState({ valid: false, status: "empty", classData: null });
  useEffect(() => {
    let active = true;
    const timer = setTimeout(async () => {
      const result = await validateMeetingCode(meetingCode);
      if (active) setCodeValidation(result);
    }, 300);
    return () => { active = false; clearTimeout(timer); };
  }, [meetingCode]);

  const matchedClass = codeValidation.classData || scheduledClass;

  // Auto-launch Host Studio if explicitly arriving via host link with room code
  useEffect(() => {
    if (!isRequestedHostEntry || !roomCode || step !== "entry") return;
    const cleanCode = String(roomCode).trim().toUpperCase();
    api.post(`/api/v2/classroom/rooms/${encodeURIComponent(cleanCode)}/start`).catch(() => {});
    setGuestToken("");
    setSessionStartTime(Date.now());
    setStep("in-class");
  }, [isRequestedHostEntry, roomCode, step]);

  // Start local webcam preview in entry step
  useEffect(() => {
    if (step !== "entry") return;
    let streamInstance = null;

    navigator.mediaDevices
      ?.getUserMedia({ video: true, audio: true })
      .then((stream) => {
        streamInstance = stream;
        setPreviewStream(stream);
        if (previewVideoRef.current) {
          previewVideoRef.current.srcObject = stream;
        }
      })
      .catch((err) => {
        console.warn("Camera preview error:", err.message);
      });

    return () => {
      if (streamInstance) {
        streamInstance.getTracks().forEach((t) => t.stop());
      }
    };
  }, [step]);

  // Ensure any lingering preview tracks are stopped as soon as we leave the entry lobby
  useEffect(() => {
    if (step !== "entry" && previewStream) {
      previewStream.getTracks().forEach((t) => t.stop());
      setPreviewStream(null);
    }
  }, [step, previewStream]);


  // Handle toggles in entry preview
  const togglePreviewCam = () => {
    if (previewStream) {
      const videoTrack = previewStream.getVideoTracks()[0];
      if (videoTrack) {
        videoTrack.enabled = !camActive;
        setCamActive(!camActive);
      }
    } else {
      setCamActive(!camActive);
    }
  };

  const togglePreviewMic = () => {
    if (previewStream) {
      const audioTrack = previewStream.getAudioTracks()[0];
      if (audioTrack) {
        audioTrack.enabled = !micActive;
        setMicActive(!micActive);
      }
    } else {
      setMicActive(!micActive);
    }
  };

  // Timer in class
  useEffect(() => {
    if (step !== "in-class" || !sessionStartTime) return;
    const interval = setInterval(() => {
      const elapsed = Math.floor((Date.now() - sessionStartTime) / 1000);
      setDurationSeconds(elapsed);
    }, 1000);
    return () => clearInterval(interval);
  }, [step, sessionStartTime]);

  // Waiting Room Subscription & Timer
  useEffect(() => {
    if (step !== "waiting-room" || !waitingRequestId) return;

    const timer = setInterval(() => {
      setWaitingSeconds((prev) => prev + 1);
    }, 1000);

    const unsubscribe = subscribeToAdmissionStatus({
      roomCode: meetingCode,
      requestId: waitingRequestId,
      onAdmitted: async (data) => {
        if (joiningRoomRef.current) return;
        const admissionTicket =
          data?.guestToken ||
          data?.admissionTicket ||
          data?.ticket ||
          (data?.status === "admitted" ? (data?.id || waitingRequestId) : "") ||
          waitingRequestId ||
          "";
        // Stay in the waiting room only until admission status is confirmed
        if (!admissionTicket) return;
        setAdmissionApproved(true);
        setJoinError("");
        joiningRoomRef.current = true;
        try {
          const joinData = await joinAdmittedGuest({
            roomCode: meetingCode,
            studentName,
            rollOrPhone: studentRollOrPhone,
            ticket: admissionTicket,
          });
          // Only the new attendance token returned by /join may be used for
          // /media-token and attendance. Never reuse the admission ticket.
          setGuestToken(joinData.guestToken);
          setSessionStartTime(Date.now());
          setStep("in-class");
          toast.success(`🎉 You've been admitted by the teacher! Attendance timer started.`);
        } catch (error) {
          joiningRoomRef.current = false;
          const message = error?.message || "Admission was approved, but the classroom could not be joined.";
          setJoinError(message);
          toast.error(message);
          return;
        }
        // Try playing subtle chime
        try {
          const ctx = new (window.AudioContext || window.webkitAudioContext)();
          const osc = ctx.createOscillator();
          const gain = ctx.createGain();
          osc.connect(gain);
          gain.connect(ctx.destination);
          osc.frequency.setValueAtTime(587.33, ctx.currentTime); // D5
          osc.frequency.setValueAtTime(880, ctx.currentTime + 0.15); // A5
          gain.gain.setValueAtTime(0.1, ctx.currentTime);
          gain.gain.exponentialRampToValueAtTime(0.01, ctx.currentTime + 0.4);
          osc.start();
          osc.stop(ctx.currentTime + 0.4);
        } catch {}
      },
      onDenied: (reason) => {
        setDenialReason(reason || "The teacher declined your admission request.");
        setStep("denied");
        toast.error(reason || "Host declined your entry request.");
      },
    });

    return () => {
      clearInterval(timer);
      unsubscribe();
    };
  }, [step, waitingRequestId, meetingCode, studentName, studentRollOrPhone, joinRetryNonce]);

  const formatTimer = (totalSec) => {
    const hrs = Math.floor(totalSec / 3600);
    const mins = Math.floor((totalSec % 3600) / 60);
    const secs = totalSec % 60;
    const pad = (n) => String(n).padStart(2, "0");
    if (hrs > 0) return `${pad(hrs)}:${pad(mins)}:${pad(secs)}`;
    return `${pad(mins)}:${pad(secs)}`;
  };

  // Join Classroom Submission & Validation
  const handleJoin = async (e) => {
    e?.preventDefault();
    const cleanCode = meetingCode.trim().toUpperCase();
    const cleanName = studentName.trim();

    // 1. Validate Code
    if (!cleanCode) {
      toast.error("Please enter a valid Meeting ID / Code.");
      return;
    }

    // Hosts must not be routed through public verification, waiting-room, or
    // guest-name checks. The authenticated backend start endpoint is the
    // authority for assignment, tenant ownership, schedule and room state.
    if (isHostEntry) {
      try {
        await api.post(`/api/v2/classroom/rooms/${encodeURIComponent(cleanCode)}/start`);
      } catch (error) {
        // If class is already started or active, proceed so host can enter
        console.warn("Host class start notice:", error?.message);
      }
      // Release camera and microphone hardware tracks from preview before starting LiveKit
      if (previewStream) {
        previewStream.getTracks().forEach((t) => t.stop());
        setPreviewStream(null);
      }
      setGuestToken("");
      setSessionStartTime(Date.now());
      setStep("in-class");
      toast.success("Class started. You joined as the host.");
      return;
    }

    if (!codeValidation.valid) {
      toast.error(codeValidation.error || "Please enter a valid, active meeting code.");
      return;
    }

    // Validate guest identity only for the public/student path.
    if (!cleanName) {
      toast.error("Please enter your name so the teacher can identify and admit you.");
      return;
    }

    if (cleanName.length < 2) {
      toast.error("Your name must be at least 2 characters long.");
      return;
    }

    // Save student name preference
    try {
      localStorage.setItem("directMeeting:studentName", cleanName);
    } catch {}

    // Clean up preview stream
    if (previewStream) {
      previewStream.getTracks().forEach((t) => t.stop());
      setPreviewStream(null);
    }

    // Check host admission requirement
    const settings = getRoomSettings(cleanCode);

    // Request admission (knocking)
    let admissionReq;
    try {
      admissionReq = await requestClassroomAdmission({ roomCode: cleanCode, studentName: cleanName, rollOrPhone: studentRollOrPhone.trim() });
    } catch (error) {
      toast.error(error?.message || "Unable to request entry to this class.");
      return;
    }

    const requestId = admissionReq.requestId || admissionReq.id;
    const admissionTicket = admissionReq.guestToken || admissionReq.admissionTicket || admissionReq.ticket || "";
    const effectiveTicket = admissionTicket || (settings.requireAdmission === false ? requestId : "");

    if ((String(admissionReq.status).toLowerCase() === "admitted" || settings.requireAdmission === false) && effectiveTicket) {
      try {
        const joinData = await joinAdmittedGuest({
          roomCode: cleanCode,
          studentName: cleanName,
          rollOrPhone: studentRollOrPhone,
          ticket: effectiveTicket,
        });
        setGuestToken(joinData.guestToken);
        setSessionStartTime(Date.now());
        setStep("in-class");
        toast.success(`Joined class as ${cleanName}! Attendance timer started.`);
      } catch (error) {
        toast.error(error?.message || "You were admitted, but the classroom could not be joined.");
      }
    } else {
      // Place in Host Waiting Room
      setAdmissionApproved(false);
      setJoinError("");
      setWaitingRequestId(requestId);
      setWaitingSeconds(0);
      setStep("waiting-room");
      toast.info("Request sent to teacher! Waiting for host admission approval...");
    }
  };

  // Cancel Knock / Leave Waiting Room
  const handleCancelKnock = () => {
    if (waitingRequestId && meetingCode) {
      cancelClassroomAdmission(meetingCode, waitingRequestId);
    }
    setStep("entry");
    toast.info("Admission request cancelled.");
  };

  // Leave Classroom & Record Attendance
  const handleLeaveClass = () => {
    const confirm = window.confirm("Are you sure you want to leave this class? Your attendance duration will be finalized.");
    if (!confirm) return;

    if (isHostEntry) {
      setStep("left");
      toast.info("You left the classroom. The session remains available to participants.");
      return;
    }

    const leftAtTime = new Date().toISOString();
    const joinedAtTime = sessionStartTime ? new Date(sessionStartTime).toISOString() : new Date().toISOString();
    const finalSecs = durationSeconds;
    const finalMins = Math.max(1, Math.round(finalSecs / 60));

    const record = {
      meetingId: meetingCode.trim().toUpperCase(),
      topic: matchedClass?.topic || "Live Coaching Session",
      subject: matchedClass?.subject || "Tuition Lecture",
      studentName: studentName.trim(),
      phoneOrRoll: studentRollOrPhone.trim() || "Guest",
      joinedAt: joinedAtTime,
      leftAt: leftAtTime,
      durationSeconds: finalSecs,
      durationMinutes: finalMins,
      durationFormatted: formatTimer(finalSecs),
      status: "ATTENDED",
      loggedToDatabase: true,
    };

    api.post(`/api/v2/classroom/rooms/${encodeURIComponent(meetingCode)}/attendance`, record, { skipAuth: true })
      .catch((error) => toast.error(error?.message || "Attendance could not be saved."));

    setCompletedRecord(record);
    setStep("left");
    toast.info(`You left the session. Total attended duration: ${formatTimer(finalSecs)}`);
  };

  const handleEndClass = async () => {
    const confirmed = window.confirm("End this class for everyone? New joins will be blocked.");
    if (!confirmed) return;
    try {
      await api.delete(`/api/v2/classroom/rooms/${encodeURIComponent(meetingCode)}`);
      setStep("left");
      toast.success("Class ended for all participants.");
    } catch (error) {
      toast.error(error?.message || "Unable to end this class.");
    }
  };

  const handleSendMessage = async (e) => {
    e?.preventDefault();
    const clean = newChatText.trim();
    if (!clean) return;
    try {
      if (!classroomDataSenderRef.current) throw new Error("Classroom connection is not ready.");
      await classroomDataSenderRef.current({ type: "CHAT", sender: studentName || "Participant", text: clean });
      setChatMessages((prev) => [...prev, {
        sender: studentName || "Me",
        text: clean,
        time: new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
      }]);
      setNewChatText("");
    } catch (error) {
      toast.error(`Message not sent: ${error?.message || "Please reconnect."}`);
    }
  };

  const copyMeetingCode = () => {
    navigator.clipboard?.writeText(meetingCode);
    toast.success("Meeting Code copied to clipboard!");
  };

  // -------------------------------------------------------------
  // RENDER STEP 1: PRE-JOIN ENTRY LOUNGE
  // -------------------------------------------------------------
  if (step === "entry") {
    return (
      <div className={`min-h-screen py-10 px-4 sm:px-6 lg:px-8 ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-gradient-to-b from-slate-50 via-white to-sky-50/30 text-slate-900"}`}>
        <div className="mx-auto max-w-5xl">
          {/* Header & Logo */}
          <div className="text-center mb-8">
            <Link
              to="/"
              className="inline-flex items-center gap-3 group transition hover:opacity-90 mb-4"
              title="Return to ClassNX Home"
            >
              <img
                src={`${process.env.PUBLIC_URL || ""}/logo.png`}
                alt="ClassNX Logo"
                className="h-12 w-12 rounded-2xl object-cover shadow-md transition group-hover:scale-105"
              />
              <div className="text-left leading-none">
                <span className="block text-2xl font-black tracking-tight text-slate-900 dark:text-white">
                  Class<span className="text-blue-600">NX</span>
                </span>
                <span className="mt-1 flex items-center gap-1.5 text-[8px] font-bold uppercase tracking-[0.24em] text-slate-400">
                  <span className="h-px w-3 bg-blue-500" />
                  <span>Learn</span>
                  <span className="text-blue-500">&bull;</span>
                  <span>Grow</span>
                  <span className="text-blue-500">&bull;</span>
                  <span>Excel</span>
                  <span className="h-px w-3 bg-blue-500" />
                </span>
              </div>
            </Link>

            <div className="flex items-center justify-center gap-2 mb-2">
              <span className="inline-flex items-center gap-1.5 rounded-full border border-sky-500/30 bg-sky-500/10 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-sky-600 dark:text-sky-400">
                <FaVideo className="text-xs" /> Direct Classroom Entry
              </span>
              <ClassNXBrandSignature compact />
            </div>
            <h1 className="mt-2 text-3xl sm:text-4xl font-black tracking-tight">
              Join Live Tuition Lecture
            </h1>
            <p className="mt-2 text-sm text-slate-500 max-w-lg mx-auto">
              No login required. Enter your lecture code and your name. Validated sessions feature host admission protection and automatic attendance logging.
            </p>
          </div>

          <div className="grid md:grid-cols-12 gap-8 items-start">
            {/* Left: Device Setup with LiveKit/Session media permissions */}
            <div className="md:col-span-6 space-y-4">
              <ClassroomDeviceSetup
                camActive={camActive}
                micActive={micActive}
                onToggleCam={togglePreviewCam}
                onToggleMic={togglePreviewMic}
                previewStream={previewStream}
                isDarkMode={isDarkMode}
                allowStudentAudio={matchedClass?.allowStudentAudio ?? true}
                allowStudentVideo={matchedClass?.allowStudentVideo ?? false}
                isHost={isHostEntry}
              />
            </div>

            {/* Right: Meeting Join Form */}
            <div className="md:col-span-6">
              <div className={`rounded-3xl border p-6 sm:p-8 shadow-xl ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
                <form onSubmit={handleJoin} className="space-y-5">
                  <div>
                    <div className="flex items-center justify-between mb-1.5">
                      <label className="block text-xs font-bold uppercase tracking-wider text-slate-400">
                        Meeting Code / ID <span className="text-rose-500">*</span>
                      </label>
                      {meetingCode && !isScheduledEntry && (
                        <button
                          type="button"
                          onClick={() => setMeetingCode("")}
                          className="text-xs text-slate-400 hover:text-slate-600"
                        >
                          Clear
                        </button>
                      )}
                    </div>
                    <div className="relative">
                      <input
                        type="text"
                        value={meetingCode}
                        onChange={(e) => setMeetingCode(e.target.value.toUpperCase())}
                        readOnly={isScheduledEntry}
                        placeholder="e.g. NX-JEE-9041"
                        className={`w-full rounded-2xl border px-4 py-3 text-base font-bold tracking-wider uppercase transition focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10 ${
                          !meetingCode
                            ? isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-slate-50 text-slate-900"
                            : codeValidation.valid
                            ? "border-emerald-500 bg-emerald-50/10 text-emerald-600 dark:text-emerald-400"
                            : "border-rose-500 bg-rose-50/10 text-rose-600 dark:text-rose-400"
                        }`}
                        required
                      />
                      {isScheduledEntry && <p className="mt-2 text-[11px] font-semibold text-sky-600 dark:text-sky-400">Room selected from your tuition schedule. Its access is verified by the server.</p>}
                    </div>

                    {/* Dynamic Real-time Code Validation Feedback */}
                    {meetingCode && (
                      <div className="mt-2.5">
                        {codeValidation.status === "not_found" && (
                          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-600 dark:text-rose-400 flex items-start gap-2">
                            <FaTimesCircle className="mt-0.5 shrink-0" />
                            <div>
                              <p className="font-bold">Invalid Meeting Code</p>
                              <p className="text-[11px] opacity-90">{codeValidation.error}</p>
                            </div>
                          </div>
                        )}

                        {codeValidation.status === "invalid_format" && (
                          <div className="rounded-xl border border-amber-500/30 bg-amber-500/10 p-2.5 text-xs text-amber-600 dark:text-amber-400 flex items-center gap-2">
                            <FaExclamationTriangle className="shrink-0" />
                            <span>{codeValidation.error}</span>
                          </div>
                        )}

                        {codeValidation.status === "ended" && (
                          <div className="rounded-xl border border-rose-500/30 bg-rose-500/10 p-3 text-xs text-rose-600 dark:text-rose-400 flex items-start gap-2">
                            <FaTimesCircle className="mt-0.5 shrink-0" />
                            <div>
                              <p className="font-bold">Lecture Has Ended</p>
                              <p className="text-[11px] opacity-90">{codeValidation.error}</p>
                            </div>
                          </div>
                        )}

                        {codeValidation.status === "scheduled" && matchedClass && (
                          <div className="rounded-xl border border-sky-500/30 bg-sky-500/10 p-3 text-xs text-sky-600 dark:text-sky-400">
                            <p className="font-bold flex items-center gap-1.5">
                              <FaClock /> Scheduled Class:
                            </p>
                            <p className="font-semibold text-slate-800 dark:text-slate-200 mt-0.5">{matchedClass.topic}</p>
                            <p className="text-slate-500 mt-0.5">
                              {matchedClass.subject} • Faculty: {matchedClass.teacherName} • Time: {matchedClass.startTime} - {matchedClass.endTime}
                            </p>
                          </div>
                        )}

                        {codeValidation.status === "live" && matchedClass && (
                          <div className="rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3 text-xs text-emerald-600 dark:text-emerald-400">
                            <div className="flex items-center justify-between">
                              <p className="font-bold flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                                Class is LIVE:
                              </p>
                              <span className="text-[10px] font-extrabold uppercase rounded bg-emerald-500 text-white px-2 py-0.5">
                                Open for Admission
                              </span>
                            </div>
                            <p className="font-semibold text-slate-800 dark:text-slate-200 mt-1">{matchedClass.topic}</p>
                            <p className="text-slate-500 mt-0.5">
                              {matchedClass.subject} • Faculty: {matchedClass.teacherName}
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                      {isHostEntry ? "Host Full Name" : "Student Full Name"} <span className="text-rose-500">*</span>
                    </label>
                    <input
                      type="text"
                      value={studentName}
                      onChange={(e) => setStudentName(e.target.value)}
                      placeholder="Enter your full name"
                      className={`w-full rounded-2xl border px-4 py-3 text-sm font-semibold transition focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-slate-50 text-slate-900"}`}
                      required
                    />
                    <p className="mt-1 text-[11px] text-slate-400">
                      {isHostEntry
                        ? "Prefilled from your authenticated tuition session. Host access is verified by the server."
                        : "Required for teacher admission verification & attendance tracking."}
                    </p>
                  </div>

                  <div>
                    <label className="block text-xs font-bold uppercase tracking-wider text-slate-400 mb-1.5">
                      Roll Number or Mobile Number (Optional)
                    </label>
                    <input
                      type="text"
                      value={studentRollOrPhone}
                      onChange={(e) => setStudentRollOrPhone(e.target.value)}
                      placeholder="e.g. APX-001 or +91 98765 43210"
                      className={`w-full rounded-2xl border px-4 py-3 text-sm font-semibold transition focus:border-sky-500 focus:outline-none focus:ring-4 focus:ring-sky-500/10 ${isDarkMode ? "border-slate-700 bg-slate-950 text-slate-100" : "border-slate-300 bg-slate-50 text-slate-900"}`}
                    />
                  </div>

                  <button
                    type="submit"
                    disabled={Boolean(!isHostEntry && meetingCode && !codeValidation.valid)}
                    className={`w-full flex items-center justify-center gap-2 rounded-2xl py-3.5 px-4 font-bold text-white shadow-lg transition active:scale-[0.99] ${
                      !isHostEntry && meetingCode && !codeValidation.valid
                        ? "bg-slate-500 cursor-not-allowed opacity-60"
                        : "bg-gradient-to-r from-sky-600 to-blue-600 hover:from-sky-700 hover:to-blue-700 shadow-sky-600/25 hover:shadow-sky-600/35"
                    }`}
                  >
                    <FaDoorOpen className="text-lg" />
                    {!isHostEntry && meetingCode && !codeValidation.valid
                      ? "Enter Valid Meeting Code"
                      : isHostEntry ? "Start / Enter Room" : "Knock & Request Admission"}
                  </button>
                </form>
              </div>
            </div>
          </div>

          {/* ClassNX platform signature */}
          <div className="mt-12 border-t border-slate-200 pt-7 text-center dark:border-slate-800">
            <ClassNXBrandSignature />
            <p className="mx-auto mt-3 max-w-md text-[10px] leading-5 text-slate-400">
              Protected by verified host admission and automatic attendance tracking.
              <span className="mx-1.5 text-slate-300 dark:text-slate-700">&bull;</span>
              &copy; {new Date().getFullYear()} ClassNX
            </p>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // RENDER STEP 2: WAITING ROOM ("KNOCKING" STATE)
  // -------------------------------------------------------------
  if (step === "waiting-room") {
    return (
      <div className={`min-h-screen py-12 px-4 sm:px-6 lg:px-8 flex items-center justify-center ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-gradient-to-b from-slate-50 to-sky-50 text-slate-900"}`}>
        <div className="w-full max-w-lg">
          <div className={`rounded-3xl border p-8 sm:p-10 shadow-2xl text-center relative overflow-hidden ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
            
            {/* Ambient Background Glow */}
            <div className="absolute -top-24 -left-24 h-48 w-48 rounded-full bg-sky-500/10 blur-3xl pointer-events-none" />
            <div className="absolute -bottom-24 -right-24 h-48 w-48 rounded-full bg-indigo-500/10 blur-3xl pointer-events-none" />

            {/* Header Branding */}
            <div className="flex items-center justify-center gap-2 mb-6">
              <img
                src={`${process.env.PUBLIC_URL || ""}/logo.png`}
                alt="ClassNX Logo"
                className="h-8 w-8 rounded-xl object-cover shadow-sm"
              />
              <span className="font-black text-base text-slate-900 dark:text-white">Class<span className="text-blue-600">NX</span></span>
              <span className="text-xs text-slate-400">&bull;</span>
              <span className="text-xs font-semibold text-sky-600 dark:text-sky-400">Live Classroom Waiting Room</span>
            </div>

            {/* Pulsing Radar Beacon */}
            <div className="relative mx-auto my-6 flex h-24 w-24 items-center justify-center">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-sky-400 opacity-25" />
              <span className="animate-pulse absolute inline-flex h-20 w-20 rounded-full bg-sky-500/20" />
              <div className="relative flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-tr from-sky-600 to-indigo-600 text-white text-2xl shadow-xl shadow-sky-600/30">
                <FaUserClock />
              </div>
            </div>

            <span className={`inline-block rounded-full px-3.5 py-1 text-xs font-bold uppercase tracking-wider ${admissionApproved ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-950/60 dark:text-emerald-300" : "bg-sky-100 text-sky-700 dark:bg-sky-950/60 dark:text-sky-300"}`}>
              {admissionApproved ? "Admission Approved" : "Admission Request Pending"}
            </span>

            <h2 className="mt-3 text-2xl sm:text-3xl font-black">
              {admissionApproved ? "Connecting to Your Classroom" : "Waiting for Host to Admit You"}
            </h2>

            <p className="mt-2 text-xs sm:text-sm text-slate-500 max-w-md mx-auto">
              {admissionApproved
                ? "The host admitted you. ClassNX is securely exchanging your admission ticket and connecting your media."
                : <>The faculty host (<strong className="text-slate-800 dark:text-slate-200">{matchedClass?.teacherName || "Course Instructor"}</strong>) has been notified. You will be automatically connected as soon as they click <strong>Admit</strong>.</>}
            </p>

            {joinError && (
              <div className="mx-auto mt-4 max-w-md rounded-2xl border border-rose-300 bg-rose-50 p-3 text-left text-xs text-rose-700 dark:border-rose-900/60 dark:bg-rose-950/30 dark:text-rose-300">
                <p className="font-bold">Admission succeeded, but joining failed</p>
                <p className="mt-1 break-words">{joinError}</p>
                <button
                  type="button"
                  onClick={() => {
                    joiningRoomRef.current = false;
                    setJoinError("");
                    setJoinRetryNonce((value) => value + 1);
                  }}
                  className="mt-3 rounded-xl bg-rose-600 px-3 py-2 font-bold text-white hover:bg-rose-700"
                >
                  Retry Joining Classroom
                </button>
              </div>
            )}

            {/* Lecture Details Card */}
            <div className={`mt-6 rounded-2xl border p-4 text-left text-xs space-y-2.5 ${isDarkMode ? "border-slate-800 bg-slate-950/60" : "border-slate-100 bg-slate-50"}`}>
              <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-800">
                <span className="text-slate-400 font-semibold uppercase text-[10px]">Lecture Topic:</span>
                <span className="font-bold text-slate-900 dark:text-slate-100 truncate max-w-[220px]">
                  {matchedClass?.topic || "Live Tuition Lecture"}
                </span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-800">
                <span className="text-slate-400 font-semibold uppercase text-[10px]">Meeting ID:</span>
                <span className="font-mono font-bold text-sky-500">{meetingCode}</span>
              </div>
              <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-800">
                <span className="text-slate-400 font-semibold uppercase text-[10px]">Your Name:</span>
                <span className="font-bold text-slate-800 dark:text-slate-200">
                  {studentName} {studentRollOrPhone ? `(${studentRollOrPhone})` : ""}
                </span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-slate-400 font-semibold uppercase text-[10px]">Time in Waiting Queue:</span>
                <span className="font-mono font-extrabold text-sky-500 text-sm">
                  {formatTimer(waitingSeconds)}
                </span>
              </div>
            </div>

            {/* Device readiness status */}
            <div className="mt-4 flex items-center justify-center gap-4 text-xs text-slate-400">
              <span className={`inline-flex items-center gap-1.5 ${micActive ? "text-emerald-500 font-semibold" : "text-slate-500"}`}>
                <FaMicrophone /> {micActive ? "Mic Ready" : "Mic Muted"}
              </span>
              <span>&bull;</span>
              <span className={`inline-flex items-center gap-1.5 ${camActive ? "text-emerald-500 font-semibold" : "text-slate-500"}`}>
                <FaVideo /> {camActive ? "Camera Ready" : "Camera Off"}
              </span>
            </div>

            {/* Cancel Button */}
            <div className="mt-8">
              <button
                type="button"
                onClick={handleCancelKnock}
                className="w-full inline-flex items-center justify-center gap-2 rounded-2xl border border-slate-300 dark:border-slate-700 py-3 text-xs font-bold text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition"
              >
                <FaArrowLeft /> Cancel & Leave Waiting Room
              </button>
            </div>
          </div>
        </div>

        {/* Floating Host Controls (if teacher testing in same browser) */}
        {isHostEntry && (
          <HostWaitingRoomControls roomCode={meetingCode} isHost={true} />
        )}
      </div>
    );
  }

  // -------------------------------------------------------------
  // RENDER STEP 3: ADMISSION DECLINED BY HOST
  // -------------------------------------------------------------
  if (step === "denied") {
    return (
      <div className={`min-h-screen py-16 px-4 sm:px-6 lg:px-8 flex items-center justify-center ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-slate-50 text-slate-900"}`}>
        <div className="w-full max-w-md">
          <div className={`rounded-3xl border p-8 shadow-2xl text-center ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
            <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-rose-500/10 text-rose-500 text-4xl mb-4">
              <FaTimesCircle />
            </div>

            <span className="inline-block rounded-full bg-rose-100 dark:bg-rose-950/60 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-rose-700 dark:text-rose-300">
              Admission Declined
            </span>

            <h2 className="mt-3 text-2xl font-black">
              Entry Declined by Host
            </h2>

            <p className="mt-2 text-xs sm:text-sm text-slate-500">
              {denialReason || "The faculty host declined your request to join this session."}
            </p>

            <div className={`mt-5 rounded-2xl border p-4 text-left text-xs ${isDarkMode ? "border-slate-800 bg-slate-950/60 text-slate-400" : "border-slate-100 bg-slate-50 text-slate-600"}`}>
              <p className="font-semibold text-slate-700 dark:text-slate-300">Possible reasons:</p>
              <ul className="list-disc list-inside mt-1.5 space-y-1 text-[11px] text-slate-500">
                <li>This batch is restricted to enrolled tuition students only.</li>
                <li>Your student name or roll number was not recognized on the roster.</li>
                <li>The lecture has progressed past the allowed entry cutoff time.</li>
              </ul>
            </div>

            <div className="mt-6 flex flex-col sm:flex-row gap-3">
              <button
                type="button"
                onClick={() => setStep("entry")}
                className="flex-1 rounded-2xl bg-sky-600 py-3 text-xs font-bold text-white hover:bg-sky-500 transition shadow"
              >
                Try Again
              </button>
              <Link
                to="/"
                className="flex-1 rounded-2xl border border-slate-300 dark:border-slate-700 py-3 text-xs font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition text-center"
              >
                Back to Home
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // -------------------------------------------------------------
  // RENDER STEP 4: ACTIVE LIVE CLASSROOM ROOM
  // -------------------------------------------------------------
  if (step === "in-class") {
    if (isHostEntry) {
      return (
        <ClassroomManagementStudio
          roomCode={meetingCode}
          scheduledClass={matchedClass}
          isHost={true}
          facultyName={studentName}
          onClose={() => navigate(-1)}
        />
      );
    }

    return (
      <div className="min-h-screen bg-slate-950 text-white flex flex-col">
        {/* Top Control Bar */}
        <header className="h-16 border-b border-slate-800 bg-slate-900/90 px-4 flex items-center justify-between backdrop-blur-md">
          <div className="flex items-center gap-3">
            <Link to="/" className="flex items-center gap-2" title="ClassNX">
              <img
                src={`${process.env.PUBLIC_URL || ""}/logo.png`}
                alt="ClassNX Logo"
                className="h-8 w-8 rounded-xl object-cover shadow-sm"
              />
              <span className="font-black text-sm tracking-tight hidden sm:inline">
                Class<span className="text-blue-500">NX</span>
              </span>
            </Link>
            <div className="h-4 w-px bg-slate-700 hidden sm:block" />
            <div>
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-500 animate-pulse" />
                <h2 className="text-xs sm:text-sm font-bold truncate max-w-[200px] sm:max-w-xs md:max-w-md">
                  {matchedClass?.topic || "Live Tuition Lecture"}
                </h2>
              </div>
              <p className="text-[10px] text-slate-400 hidden sm:block">
                {matchedClass?.subject || "Subject"} &bull; {matchedClass?.teacherName || "Faculty"} &bull; Room: {meetingCode}
              </p>
            </div>
          </div>

          {/* Center Timer */}
          <div className="hidden sm:flex items-center gap-2 rounded-full bg-slate-800/90 px-3.5 py-1 text-xs font-mono font-bold text-sky-400 border border-slate-700">
            <FaClock className="text-slate-400" />
            <span>Duration: {formatTimer(durationSeconds)}</span>
          </div>

          {/* Right Action Icons */}
          <div className="flex items-center gap-2">
            <button
              type="button"
              onClick={copyMeetingCode}
              className="rounded-full border border-slate-700 bg-slate-800 p-2.5 text-xs text-slate-300 hover:bg-slate-700 transition"
              title="Copy Meeting ID"
            >
              <FaCopy />
            </button>

            <button
              type="button"
              onClick={() => {
                setShowParticipants(!showParticipants);
                if (showChat) setShowChat(false);
              }}
              className={`rounded-full p-2.5 text-xs transition relative ${showParticipants ? "bg-sky-500 text-white" : "border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700"}`}
              title="Participants"
            >
              <FaUsers />
              <span className="absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full bg-emerald-500 text-[9px] font-bold text-white">
                {1 + (matchedClass?.attendees?.length || 0)}
              </span>
            </button>

            <button
              type="button"
              onClick={() => {
                setShowChat(!showChat);
                if (showParticipants) setShowParticipants(false);
              }}
              className={`rounded-full p-2.5 text-xs transition relative ${showChat ? "bg-sky-500 text-white" : "border border-slate-700 bg-slate-800 text-slate-300 hover:bg-slate-700"}`}
              title="Lecture Chat"
            >
              <FaComments />
            </button>

            {isHostEntry && (
              <button
                type="button"
                onClick={handleEndClass}
                className="inline-flex items-center gap-1.5 rounded-full bg-red-700 px-4 py-2 text-xs font-bold text-white hover:bg-red-800 transition"
              >
                <FaTimesCircle /> End Class
              </button>
            )}

            <button
              type="button"
              onClick={handleLeaveClass}
              className="inline-flex items-center gap-1.5 rounded-full bg-rose-600 px-4 py-2 text-xs font-bold text-white hover:bg-rose-700 transition shadow-lg shadow-rose-600/30"
            >
              <FaSignOutAlt /> {isHostEntry ? "Leave" : "Leave Class"}
            </button>
          </div>
        </header>

        {/* Main Body: Video Stage + Side Panels */}
        <div className="flex-1 flex overflow-hidden relative">
          {/* Main Stage: WebRTC / Video Classroom */}
          <div className="flex-1 p-3 sm:p-5 overflow-y-auto flex flex-col">
            <div className="flex-1 min-h-[380px] rounded-3xl border border-slate-800 bg-slate-900/60 overflow-hidden flex flex-col">
              <LiveKitClassroom
                meetingCode={meetingCode}
                guestToken={guestToken}
                isHost={isHostEntry}
                displayName={studentName}
                initialMicEnabled={micActive}
                initialCamEnabled={camActive}
                onLeave={handleLeaveClass}
                onToggleChat={() => {
                  setShowChat((prev) => !prev);
                  if (showParticipants) setShowParticipants(false);
                }}
                onToggleParticipants={() => {
                  setShowParticipants((prev) => !prev);
                  if (showChat) setShowChat(false);
                }}
                showChat={showChat}
                showParticipants={showParticipants}
                onParticipantsChanged={setLiveParticipants}
                onDataSenderReady={(sender) => { classroomDataSenderRef.current = sender; }}
                onChatMessage={(message) => {
                  setChatMessages((previous) => [...previous, {
                    id: message.id,
                    sender: message.sender || "Participant",
                    text: message.text,
                    time: new Date(message.sentAt || Date.now()).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }),
                  }]);
                  toast.info(`${message.sender || "Participant"}: ${message.text}`);
                }}
                allowStudentAudio={matchedClass?.allowStudentAudio ?? true}
                allowStudentVideo={matchedClass?.allowStudentVideo ?? false}
              />
            </div>

            {/* Bottom Status bar */}
            <div className="mt-3 flex items-center justify-between text-xs text-slate-400 px-2">
              <div className="flex items-center gap-2">
                <span className="h-2 w-2 rounded-full bg-emerald-500" />
                <span>Connected as <strong className="text-white">{studentName}</strong></span>
              </div>
              <div className="sm:hidden font-mono text-sky-400">
                {formatTimer(durationSeconds)}
              </div>
              <div className="text-slate-500 hidden sm:block">
                Attendance is being automatically logged to ClassNX.
              </div>
            </div>
          </div>

          {/* Right Sliding Drawer: Attendees */}
          {showParticipants && (
            <div className="absolute inset-0 w-full border-l border-slate-800 bg-slate-900 p-4 flex flex-col z-40 animate-in slide-in-from-right duration-200 sm:static sm:w-72">
              <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <FaUsers className="text-sky-400" /> Attendees ({liveParticipants.length})
                </h3>
                <button
                  type="button"
                  onClick={() => setShowParticipants(false)}
                  className="text-xs text-slate-400 hover:text-white"
                >
                  ✕
                </button>
              </div>
              <div className="flex-1 overflow-y-auto py-3 space-y-2.5">
                {liveParticipants.map((participant) => (
                  <div key={participant.identity} className={`flex items-center gap-2.5 rounded-xl border p-2.5 text-xs ${participant.isLocal ? "border-sky-500/30 bg-sky-500/10" : "border-slate-800 bg-slate-950"}`}>
                    <div className={`h-7 w-7 rounded-full text-white font-bold flex items-center justify-center ${participant.isLocal ? "bg-sky-500" : "bg-slate-700"}`}>
                      {participant.name.charAt(0).toUpperCase()}
                    </div>
                    <div className="min-w-0 flex-1">
                      <p className="font-semibold text-slate-200 truncate">
                        {participant.name}{participant.isLocal ? " (You)" : ""}
                      </p>
                      <p className="text-[10px] text-slate-500">In Class</p>
                    </div>
                    <span className="text-[10px] font-bold text-emerald-400">Active</span>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Right Sliding Drawer: Chat */}
          {showChat && (
            <div className="absolute inset-0 w-full border-l border-slate-800 bg-slate-900 p-4 flex flex-col z-40 animate-in slide-in-from-right duration-200 sm:static sm:w-80">
              <div className="flex items-center justify-between pb-3 border-b border-slate-800">
                <h3 className="text-sm font-bold flex items-center gap-2">
                  <FaComments className="text-sky-400" /> Lecture Chat
                </h3>
                <button
                  type="button"
                  onClick={() => setShowChat(false)}
                  className="text-xs text-slate-400 hover:text-white"
                >
                  ✕
                </button>
              </div>

              <div className="flex-1 overflow-y-auto py-3 space-y-3">
                {chatMessages.map((msg, idx) => (
                  <div
                    key={idx}
                    className={`rounded-2xl p-3 text-xs ${
                      msg.sender === studentName
                        ? "bg-sky-600/20 border border-sky-500/30 ml-4 text-right"
                        : msg.sender === "System"
                        ? "bg-slate-800/60 border border-slate-700/50 text-slate-400 text-center text-[11px]"
                        : "bg-slate-800/90 border border-slate-700 mr-4 text-left"
                    }`}
                  >
                    {msg.sender !== "System" && (
                      <div className="flex items-center justify-between text-[10px] font-bold text-slate-400 mb-1">
                        <span>{msg.sender}</span>
                        <span>{msg.time}</span>
                      </div>
                    )}
                    <p className="text-slate-100">{msg.text}</p>
                  </div>
                ))}
              </div>

              <form onSubmit={handleSendMessage} className="pt-3 border-t border-slate-800 flex gap-2">
                <input
                  type="text"
                  value={newChatText}
                  onChange={(e) => setNewChatText(e.target.value)}
                  placeholder="Ask teacher a question..."
                  className="flex-1 rounded-xl border border-slate-700 bg-slate-950 px-3 py-2 text-xs text-slate-100 focus:outline-none focus:border-sky-500"
                />
                <button
                  type="submit"
                  className="rounded-xl bg-sky-600 px-3 py-2 text-xs font-bold text-white hover:bg-sky-500"
                >
                  <FaPaperPlane />
                </button>
              </form>
            </div>
          )}
        </div>

        {/* Floating Host Controls (if teacher testing in same browser) */}
        {isHostEntry && (
          <HostWaitingRoomControls roomCode={meetingCode} isHost={true} />
        )}
      </div>
    );
  }

  // -------------------------------------------------------------
  // RENDER STEP 5: POST-CLASS ATTENDANCE RECEIPT
  // -------------------------------------------------------------
  return (
    <div className={`min-h-screen py-16 px-4 sm:px-6 lg:px-8 flex items-center justify-center ${isDarkMode ? "bg-slate-950 text-slate-100" : "bg-gradient-to-b from-slate-50 to-sky-50/50 text-slate-900"}`}>
      <div className="w-full max-w-lg">
        <div className={`rounded-3xl border p-8 sm:p-10 shadow-2xl text-center ${isDarkMode ? "border-slate-800 bg-slate-900" : "border-slate-200 bg-white"}`}>
          <div className="flex items-center justify-center gap-2 mb-6">
            <img
              src={`${process.env.PUBLIC_URL || ""}/logo.png`}
              alt="ClassNX Logo"
              className="h-8 w-8 rounded-xl object-cover shadow-sm"
            />
            <span className="font-black text-base text-slate-900 dark:text-white">Class<span className="text-blue-600">NX</span></span>
            <span className="text-xs text-slate-400">&bull;</span>
            <span className="text-xs font-semibold text-slate-500 dark:text-slate-400">Official Attendance Receipt</span>
          </div>

          <div className="mx-auto flex h-20 w-20 items-center justify-center rounded-3xl bg-emerald-500/10 text-emerald-500 text-4xl mb-5 shadow-inner">
            <FaCheckCircle />
          </div>

          <span className="inline-block rounded-full bg-emerald-100 dark:bg-emerald-950/60 px-3.5 py-1 text-xs font-bold uppercase tracking-wider text-emerald-700 dark:text-emerald-300">
            Attendance Verified & Logged
          </span>

          <h2 className="mt-3 text-2xl sm:text-3xl font-black">
            Lecture Completed!
          </h2>
          <p className="mt-2 text-sm text-slate-500">
            Thank you for attending. Your participation metrics have been recorded in the institute database.
          </p>

          {/* Attendance Receipt Summary Box */}
          <div className={`mt-6 rounded-2xl border p-5 text-left text-sm space-y-3 ${isDarkMode ? "border-slate-800 bg-slate-950/60" : "border-slate-100 bg-slate-50"}`}>
            <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-800">
              <span className="text-xs text-slate-400 font-semibold uppercase">Student Name:</span>
              <span className="font-bold text-slate-900 dark:text-slate-100">{completedRecord?.studentName || studentName}</span>
            </div>

            <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-800">
              <span className="text-xs text-slate-400 font-semibold uppercase">Class / Topic:</span>
              <span className="font-semibold text-right text-slate-700 dark:text-slate-300 truncate max-w-[200px]">
                {completedRecord?.topic || "Tuition Lecture"}
              </span>
            </div>

            <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-800">
              <span className="text-xs text-slate-400 font-semibold uppercase">Meeting ID:</span>
              <span className="font-mono font-bold text-sky-600 dark:text-sky-400">{completedRecord?.meetingId || meetingCode}</span>
            </div>

            <div className="flex justify-between items-center pb-2 border-b border-slate-200 dark:border-slate-800">
              <span className="text-xs text-slate-400 font-semibold uppercase">Duration Stayed:</span>
              <span className="font-extrabold text-emerald-600 dark:text-emerald-400 text-base">
                {completedRecord?.durationFormatted || formatTimer(durationSeconds)} ({completedRecord?.durationMinutes || 1} min)
              </span>
            </div>

            <div className="flex justify-between items-center">
              <span className="text-xs text-slate-400 font-semibold uppercase">Attendance Status:</span>
              <span className="inline-flex items-center gap-1 font-bold text-emerald-600 dark:text-emerald-400 text-xs">
                <FaCheckCircle /> PRESENT (LOGGED)
              </span>
            </div>
          </div>

          <div className="mt-8 flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              onClick={() => {
                setStep("entry");
                setMeetingCode("");
                setDurationSeconds(0);
              }}
              className="flex-1 rounded-2xl border border-slate-300 dark:border-slate-700 py-3 text-sm font-bold hover:bg-slate-100 dark:hover:bg-slate-800 transition"
            >
              Join Another Class
            </button>
            <Link
              to="/"
              className="flex-1 rounded-2xl bg-slate-900 dark:bg-white text-white dark:text-slate-900 py-3 text-sm font-bold hover:opacity-90 transition text-center"
            >
              Return Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
